import { useParams, Link } from 'react-router-dom';
import { videos, articleDetail } from '@/mocks/videos';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const currentVideo = videos.find((v) => v.id === id);
  const detail = id === 'v001' ? articleDetail : null;

  if (!currentVideo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-400 mb-4 block"></i>
          <p className="text-foreground-600 text-lg">视频不存在或已下架</p>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.id !== id && v.category === currentVideo.category)
    .slice(0, 4);

  const pageTitle = `${currentVideo.title} - 嫩草视频高清在线观看`;
  const pageDesc = `${currentVideo.title}，${currentVideo.description.slice(0, 100)}。嫩草视频高清流畅在线观看，无需下载APP。`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: currentVideo.title,
    description: currentVideo.description,
    image: currentVideo.cover,
    datePublished: currentVideo.updateTime,
    author: {
      '@type': 'Organization',
      name: '嫩草视频',
    },
    publisher: {
      '@type': 'Organization',
      name: '嫩草视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.nencaovideo.com/article/${id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`${currentVideo.title},${currentVideo.categoryName},嫩草视频,在线观看,高清`}
        canonicalUrl={`https://www.nencaovideo.com/article/${id}`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogImage={currentVideo.cover}
        ogType="article"
        ogUrl={`https://www.nencaovideo.com/article/${id}`}
        schema={articleSchema}
      />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: currentVideo.categoryName, href: `/category/${currentVideo.category}` },
          { label: currentVideo.title },
        ]}
      />

      <main className="w-full px-4 md:px-6 py-8">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <article className="lg:col-span-2">
              <div className="relative aspect-video rounded-xl overflow-hidden bg-foreground-950 mb-6">
                <img
                  src={currentVideo.cover}
                  alt={currentVideo.title}
                  title={`${currentVideo.title} - 嫩草视频高清在线观看`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors">
                    <i className="ri-play-fill text-white text-3xl md:text-4xl ml-1"></i>
                  </div>
                </div>
              </div>

              <h1 className="text-xl md:text-2xl font-heading text-foreground-900 mb-4">
                {currentVideo.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-6 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong className="text-foreground-800">{currentVideo.rating}</strong>
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {currentVideo.year}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {currentVideo.duration}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {currentVideo.views}次观看
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {currentVideo.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="prose prose-sm max-w-none">
                <h3 className="text-lg font-medium text-foreground-900 mb-3">影片简介</h3>
                <p className="text-foreground-700 leading-relaxed whitespace-pre-line">
                  {detail?.description || currentVideo.description}
                </p>

                {detail && (
                  <>
                    <h3 className="text-lg font-medium text-foreground-900 mb-3 mt-6">详细内容</h3>
                    <p className="text-foreground-700 leading-relaxed whitespace-pre-line">
                      {detail.content}
                    </p>

                    {detail.director && (
                      <div className="mt-4">
                        <span className="text-foreground-500 text-sm">导演：</span>
                        <span className="text-foreground-800">{detail.director}</span>
                      </div>
                    )}
                    {detail.actors && detail.actors.length > 0 && (
                      <div className="mt-2">
                        <span className="text-foreground-500 text-sm">主演：</span>
                        <span className="text-foreground-800">{detail.actors.join(' / ')}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </article>

            <aside className="lg:col-span-1">
              <div className="sticky top-20">
                <h3 className="text-lg font-heading text-foreground-900 mb-4">
                  相关推荐
                </h3>
                <div className="space-y-3">
                  {relatedVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.id}`}
                      className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <div className="w-32 h-20 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                        <img
                          src={video.cover}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                          <span className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                            {video.rating}
                          </span>
                          <span>{video.views}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="mt-6 p-4 rounded-xl bg-background-100 border border-background-200/70">
                  <h4 className="text-sm font-medium text-foreground-800 mb-2">
                    热门标签
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {['科幻', '动作', '爱情', '喜剧', '悬疑', '动画', '古装', '都市'].map(
                      (tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded-full bg-background-50 text-foreground-600 text-xs cursor-pointer hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}